import type { Profile } from "../../structs.ts";
import { authState, pdsRequest, signIn } from "../auth.ts";
import { formatError } from "../api.ts";
import { canUploadBlob } from "../permissions.ts";
import { updateProfile } from "../profile.ts";
import { blobUrl } from "../cdn.ts";
import { config } from "../config.ts";
import { patchOwnProfile } from "../own-profile.ts";

export function ProfileEditor(
  viewer: `did:${string}`,
  record: Profile | undefined,
  onSaved: (record: Profile, avatarPreview?: string) => void,
  signal: AbortSignal,
): Element {
  const trigger = <button class="profile-edit-button" type="button">Edit profile</button>;
  const dialog = <dialog class="profile-dialog"></dialog> as HTMLDialogElement;
  const name = (
    <input
      aria-label="Display name"
      maxLength={128}
      value={record?.displayName ?? ""}
    />
  ) as HTMLInputElement;
  const description = (
    <textarea aria-label="Bio" maxLength={4096}>{record?.description ?? ""}</textarea>
  ) as HTMLTextAreaElement;
  const avatar = <input aria-label="Avatar" type="file" accept="image/*" /> as HTMLInputElement;
  const status = <p class="status" role="status"></p>;
  const submit = <button type="submit">Save</button> as HTMLButtonElement;
  const close = <button class="profile-dialog-close" type="button" aria-label="Close">×</button>;
  const state = authState.get();
  const canUpload = state.status === "authenticated" && canUploadBlob(state.scopes, "image/png");
  avatar.disabled = !canUpload;

  const form = (
    <form
      class="profile-editor"
      _onsubmit={(event) => {
        event.preventDefault();
        const current = authState.get();
        if (current.status !== "authenticated" || current.did !== viewer) {
          status.textContent = "The signed-in account changed";
          return;
        }
        const file = avatar.files?.[0];
        submit.disabled = true;
        status.textContent = "Saving…";
        void updateProfile(
          viewer,
          current.scopes,
          pdsRequest,
          {
            displayName: name.value,
            description: description.value,
            ...(file && { avatar: file }),
          },
          signal,
        ).then((updated) => {
          patchOwnProfile(viewer, (view) => {
            const { displayName: _name, avatar: _avatar, ...actor } = view.actor;
            // Without a CDN the AppView links avatars through the PDS; keep its last URL until then.
            const avatar = updated.avatar
              ? blobUrl(config.cdn, viewer, updated.avatar.ref.$link) ?? view.actor.avatar
              : undefined;
            return {
              ...view,
              record: updated,
              actor: {
                ...actor,
                ...(updated.displayName !== undefined && { displayName: updated.displayName }),
                ...(avatar && { avatar }),
              },
            };
          });
          if (signal.aborted) return;
          dialog.close();
          onSaved(updated, file ? URL.createObjectURL(file) : undefined);
        }).catch((error) => {
          if (!signal.aborted) status.textContent = formatError(error);
        }).finally(() => {
          submit.disabled = false;
        });
      }}
    >
      <label>Display name{name}</label>
      <label>Bio{description}</label>
      <label>Avatar{avatar}</label>
      <button
        type="button"
        hidden={canUpload}
        _onclick={() => void signIn(viewer, location.pathname)}
      >
        Authorize avatar uploads
      </button>
      <div class="profile-editor-actions">{submit}</div>
      {status}
    </form>
  );
  close.addEventListener("click", () => dialog.close());
  dialog.append(
    <section class="profile-dialog-content">
      <header>
        <h2>Edit profile</h2>
        {close}
      </header>
      {form}
    </section>,
  );
  trigger.addEventListener("click", () => dialog.showModal());
  signal.addEventListener("abort", () => dialog.open && dialog.close(), { once: true });
  return <span class="profile-editor-control">{trigger}{dialog}</span>;
}
